/**
 * Enterprise Help System - Content Registry
 *
 * Central registry for all help content. This file manages:
 * - Content registration and lookup
 * - Route-to-content mapping
 * - Section and subsection resolution
 *
 * HOW TO ADD NEW HELP CONTENT:
 * ────────────────────────────
 *
 * 1. Create a new file in `./pages/` (e.g., `my-feature.ts`)
 * 2. Export your help content using the helper functions:
 *
 *    ```ts
 *    import { createHelpContent, createSection, createFAQ } from '../utils';
 *    import type { HelpContent } from '../types';
 *
 *    export const myFeatureHelp: HelpContent = createHelpContent({
 *      id: 'my-feature',
 *      title: 'My Feature',
 *      description: 'Help for my feature',
 *      sections: [
 *        createSection('overview', 'Overview', '<p>Content here...</p>')
 *      ],
 *      faqs: [
 *        createFAQ('faq-1', 'Question?', 'Answer.', ['tag'])
 *      ],
 *      relatedLinks: [
 *        { title: 'Related Page', href: '/related' }
 *      ]
 *    });
 *    ```
 *
 * 3. Import and register in this file:
 *    - Add import: `import { myFeatureHelp } from './pages/my-feature';`
 *    - Add to registry: `registry.register(myFeatureHelp);`
 *    - Add route mapping: `registry.mapRoute('/my-feature', 'my-feature');`
 *
 * For SECTION-LEVEL help (e.g., tabs within a page):
 * - Use content ID format: "pagename:section" (e.g., "training:library")
 * - Set parentId to link to parent content
 *
 * For FIELD-LEVEL help (tooltips):
 * - Use `registerFieldHelp()` with ID format: "page:section:field"
 */

import type { HelpContent, FieldHelp, HelpRegistry, RouteMapping } from './types';

// ============================================================================
// CONTENT IMPORTS
// ============================================================================

import {
  dashboardHelp,
  dashboardQuickLinksHelp,
  dashboardAllocationHelp,
  dashboardEffortHelp,
  dashboardNewsHelp,
} from './pages/dashboard';
import { newsHelp } from './pages/news';
import { trainingHelp } from './pages/training';
import { knowledgeHelp } from './pages/knowledge';
import { projectsHelp, newProjectDialogHelp } from './pages/projects';
import { surveysHelp, surveyCreateHelp, surveyResultsHelp } from './pages/surveys';
import { timesheetHelp, timesheetEffortReportHelp, timesheetTeamDailyHelp } from './pages/timesheet';
import { directoryHelp, directoryPeopleTeamsHelp } from './pages/directory';
import { leaveHelp, leaveApprovalsHelp, leaveWorkLocationHelp } from './pages/leave';
import {
  analyticsHelp,
  analyticsEffortHelp,
  analyticsTeamDailyHelp,
  analyticsTeamUtilizationHelp,
  analyticsTeamAvailabilityHelp,
  analyticsProjectHealthHelp,
  analyticsEffortVarianceHelp,
  analyticsTeamTrainingHelp,
  analyticsOrgUtilizationHelp,
  analyticsCrossTeamHelp,
  analyticsCostAnalysisHelp,
  analyticsProjectPortfolioHelp,
  analyticsOrgTrainingSkillsHelp,
  analyticsLeaveAttendanceHelp,
  analyticsSurveyEngagementHelp,
} from './pages/analytics';
import {
  adminHelp,
  adminUserManagementHelp,
  adminPoliciesHelp,
  adminSettingsHelp,
  adminLeaveManagementHelp,
  adminVerifiedDomainsHelp,
  adminFieldHelp,
} from './pages/admin';
import {
  resourcesHelp,
  resourcesAvailabilityHelp,
  resourcesProjectsHelp,
  resourcesUsersHelp,
} from './pages/resources';
import { tasksHelp, taskDetailHelp, storyDetailHelp } from './pages/tasks';
import { workLocationHelp } from './pages/work-location';
import { peopleTeamsHelp, peopleTeamsJoinRequestsHelp } from './pages/people-teams';
import { clientsHelp, clientFieldHelp } from './pages/clients';

// ============================================================================
// HELP REGISTRY CLASS
// ============================================================================

class HelpContentRegistry implements HelpRegistry {
  content: Record<string, HelpContent> = {};
  fieldHelp: Record<string, FieldHelp> = {};
  routeMappings: RouteMapping[] = [];

  /**
   * Register help content
   */
  register(helpContent: HelpContent): void {
    this.content[helpContent.id] = helpContent;

    // Auto-register child relationships
    if (helpContent.parentId && this.content[helpContent.parentId]) {
      const parent = this.content[helpContent.parentId];
      if (!parent.childIds) parent.childIds = [];
      if (!parent.childIds.includes(helpContent.id)) {
        parent.childIds.push(helpContent.id);
      }
    }
  }

  /**
   * Register multiple help contents
   */
  registerAll(contents: HelpContent[]): void {
    contents.forEach((content) => this.register(content));
  }

  /**
   * Register field-level help
   */
  registerFieldHelp(field: FieldHelp): void {
    this.fieldHelp[field.id] = field;
  }

  /**
   * Map a route to content ID
   */
  mapRoute(route: string, contentId: string, exact: boolean = false): void {
    this.routeMappings.push({ route, contentId, exact });
  }

  /**
   * Map multiple routes to content IDs
   */
  mapRoutes(mappings: Array<{ route: string; contentId: string; exact?: boolean }>): void {
    mappings.forEach(({ route, contentId, exact }) => {
      this.mapRoute(route, contentId, exact);
    });
  }

  /**
   * Get content by ID
   */
  getById(contentId: string): HelpContent | null {
    // Direct match
    if (this.content[contentId]) {
      return this.content[contentId];
    }

    // Try base ID (for section-specific requests that don't have dedicated content)
    const baseId = contentId.split(':')[0];
    return this.content[baseId] || null;
  }

  /**
   * Get content for a route
   */
  getForRoute(pathname: string): HelpContent | null {
    // Sort mappings by route length (most specific first)
    const sortedMappings = [...this.routeMappings].sort(
      (a, b) => b.route.length - a.route.length
    );

    // Try exact matches first
    for (const mapping of sortedMappings) {
      if (mapping.exact && pathname === mapping.route) {
        return this.content[mapping.contentId] || null;
      }
    }

    // Try prefix matches
    for (const mapping of sortedMappings) {
      if (!mapping.exact && pathname.startsWith(mapping.route)) {
        return this.content[mapping.contentId] || null;
      }
    }

    // Default to dashboard
    return this.content['dashboard'] || null;
  }

  /**
   * Get field help by ID
   */
  getFieldHelp(fieldId: string): FieldHelp | null {
    return this.fieldHelp[fieldId] || null;
  }

  /**
   * Get all content for a page (including sections)
   */
  getPageWithSections(pageId: string): {
    page: HelpContent | null;
    sections: HelpContent[];
  } {
    const page = this.content[pageId] || null;
    const sections = Object.values(this.content).filter(
      (c) => c.parentId === pageId
    );
    return { page, sections };
  }

  /**
   * Search all content
   */
  search(query: string): HelpContent[] {
    const lowerQuery = query.toLowerCase();
    return Object.values(this.content).filter(
      (content) =>
        content.title.toLowerCase().includes(lowerQuery) ||
        content.description.toLowerCase().includes(lowerQuery) ||
        content.keywords.some((k) => k.toLowerCase().includes(lowerQuery)) ||
        content.faqs.some(
          (faq) =>
            faq.question.toLowerCase().includes(lowerQuery) ||
            faq.answer.toLowerCase().includes(lowerQuery)
        )
    );
  }

  /**
   * Get all registered content IDs
   */
  getAllIds(): string[] {
    return Object.keys(this.content);
  }
}

// ============================================================================
// REGISTRY INSTANCE
// ============================================================================

const registry = new HelpContentRegistry();

// ============================================================================
// REGISTER CONTENT
// ============================================================================

// Core pages
registry.registerAll([
  dashboardHelp,
  newsHelp,
  trainingHelp,
  knowledgeHelp,
  projectsHelp,
  surveysHelp,
  timesheetHelp,
  directoryHelp,
  leaveHelp,
  analyticsHelp,
]);

// Section-level help (dashboard)
registry.registerAll([
  dashboardQuickLinksHelp,
  dashboardAllocationHelp,
  dashboardEffortHelp,
  dashboardNewsHelp,
]);

// Section-level help (projects)
registry.registerAll([
  newProjectDialogHelp,
]);

// Section-level help (surveys)
registry.registerAll([
  surveyCreateHelp,
  surveyResultsHelp,
]);

// Section-level help (timesheet)
registry.registerAll([
  timesheetEffortReportHelp,
  timesheetTeamDailyHelp,
]);

// Section-level help (directory)
registry.registerAll([
  directoryPeopleTeamsHelp,
]);

// Section-level help (leave)
registry.registerAll([
  leaveApprovalsHelp,
  leaveWorkLocationHelp,
]);

// Section-level help (analytics)
registry.registerAll([
  analyticsEffortHelp,
  analyticsTeamDailyHelp,
  analyticsTeamUtilizationHelp,
  analyticsTeamAvailabilityHelp,
  analyticsProjectHealthHelp,
  analyticsEffortVarianceHelp,
  analyticsTeamTrainingHelp,
  analyticsOrgUtilizationHelp,
  analyticsCrossTeamHelp,
  analyticsCostAnalysisHelp,
  analyticsProjectPortfolioHelp,
  analyticsOrgTrainingSkillsHelp,
  analyticsLeaveAttendanceHelp,
  analyticsSurveyEngagementHelp,
]);

// Admin
registry.register(adminHelp);
registry.registerAll([
  adminUserManagementHelp,
  adminPoliciesHelp,
  adminSettingsHelp,
  adminLeaveManagementHelp,
  adminVerifiedDomainsHelp,
]);

// Admin field help
Object.values(adminFieldHelp).forEach((field) => registry.registerFieldHelp(field));

// Resources
registry.register(resourcesHelp);
registry.registerAll([
  resourcesAvailabilityHelp,
  resourcesProjectsHelp,
  resourcesUsersHelp,
]);

// Tasks
registry.register(tasksHelp);
registry.registerAll([taskDetailHelp, storyDetailHelp]);

// Work Location
registry.register(workLocationHelp);

// People & Teams
registry.register(peopleTeamsHelp);
registry.registerAll([peopleTeamsJoinRequestsHelp]);

// Clients
registry.register(clientsHelp);
Object.values(clientFieldHelp).forEach((field) => registry.registerFieldHelp(field));

// ============================================================================
// ROUTE MAPPINGS
// ============================================================================

registry.mapRoutes([
  // Dashboard
  { route: '/', contentId: 'dashboard', exact: true },

  // News
  { route: '/news', contentId: 'news' },

  // Training
  { route: '/training', contentId: 'training' },

  // Knowledge
  { route: '/knowledge', contentId: 'knowledge' },

  // Projects & Tasks
  { route: '/projects', contentId: 'projects' },
  { route: '/tasks', contentId: 'tasks' },
  { route: '/my-tasks', contentId: 'tasks' },

  // Clients
  { route: '/clients', contentId: 'clients' },

  // Surveys
  { route: '/surveys', contentId: 'surveys' },
  { route: '/create-survey', contentId: 'surveys:create' },
  { route: '/take-survey', contentId: 'surveys' },
  { route: '/survey-results', contentId: 'surveys:results' },

  // Time Management
  { route: '/timesheet', contentId: 'timesheet' },
  { route: '/effort-report', contentId: 'timesheet:effort-report' },
  { route: '/team-daily-report', contentId: 'timesheet:team-daily' },

  // Directory & Teams
  { route: '/directory', contentId: 'directory' },
  // Leave Management
  { route: '/leave', contentId: 'leave' },
  { route: '/leave-approvals', contentId: 'leave:approvals' },

  // Analytics
  { route: '/analytics/effort', contentId: 'analytics:effort', exact: true },
  { route: '/analytics/team-daily', contentId: 'analytics:team-daily', exact: true },
  { route: '/analytics/team/utilization', contentId: 'analytics:team-utilization' },
  { route: '/analytics/team/availability', contentId: 'analytics:team-availability' },
  { route: '/analytics/team/project-health', contentId: 'analytics:project-health' },
  { route: '/analytics/team/effort-variance', contentId: 'analytics:effort-variance' },
  { route: '/analytics/team/training', contentId: 'analytics:team-training' },
  { route: '/analytics/admin/utilization', contentId: 'analytics:org-utilization' },
  { route: '/analytics/admin/cross-team', contentId: 'analytics:cross-team' },
  { route: '/analytics/admin/cost', contentId: 'analytics:cost-analysis' },
  { route: '/analytics/admin/portfolio', contentId: 'analytics:project-portfolio' },
  { route: '/analytics/admin/training-skills', contentId: 'analytics:org-training-skills' },
  { route: '/analytics/admin/leave-attendance', contentId: 'analytics:leave-attendance' },
  { route: '/analytics/admin/survey-engagement', contentId: 'analytics:survey-engagement' },
  { route: '/analytics', contentId: 'analytics' },

  // Resources
  { route: '/resources/availability', contentId: 'resources:availability' },
  { route: '/resources/projects', contentId: 'resources:projects' },
  { route: '/resources/users', contentId: 'resources:users' },
  { route: '/resources', contentId: 'resources' },

  // Work Location (standalone page)
  { route: '/work-location', contentId: 'work-location' },

  // People & Teams (standalone page)
  { route: '/people-teams', contentId: 'people-teams' },

  // Manager Dashboard
  { route: '/manager-dashboard', contentId: 'dashboard' },

  // Task & Story detail pages (prefix match for dynamic IDs)
  { route: '/tasks/task/', contentId: 'tasks:detail' },
  { route: '/tasks/story/', contentId: 'tasks:story-detail' },

  // Resource detail pages (prefix match for dynamic IDs)
  { route: '/resources/project/', contentId: 'resources:projects' },
  { route: '/resources/user/', contentId: 'resources:users' },

  // Training sub-pages (prefix match for dynamic IDs)
  { route: '/training/module/', contentId: 'training' },
  { route: '/pdp/', contentId: 'training' },

  // Admin
  { route: '/admin/teams', contentId: 'admin' },
  { route: '/admin/users', contentId: 'admin:user-management' },
  { route: '/admin/policies', contentId: 'admin:policies' },
  { route: '/admin/settings', contentId: 'admin:settings' },
  { route: '/admin/leave', contentId: 'admin:leave-management' },
  { route: '/admin/domains', contentId: 'admin:verified-domains' },
  { route: '/admin', contentId: 'admin' },
]);

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Get help content for a given route path
 */
export function getHelpContentForRoute(pathname: string): HelpContent | null {
  return registry.getForRoute(pathname);
}

/**
 * Get help content by its ID
 */
export function getHelpContentById(contentId: string): HelpContent | null {
  return registry.getById(contentId);
}

/**
 * Get field-level help by ID
 */
export function getFieldHelpById(fieldId: string): FieldHelp | null {
  return registry.getFieldHelp(fieldId);
}

/**
 * Search all help content
 */
export function searchAllHelpContent(query: string): HelpContent[] {
  return registry.search(query);
}

/**
 * Get the help registry (for advanced usage)
 */
export function getHelpRegistry(): HelpContentRegistry {
  return registry;
}

// Re-export types
export type {
  HelpContent,
  HelpSection,
  HelpFAQ,
  HelpRelatedLink,
  HelpTip,
  HelpWalkthrough,
  HelpVideo,
  HelpTroubleshooting,
  HelpGlossaryTerm,
  HelpPermission,
  FieldHelp,
  UserRole,
} from './types';

// Re-export utilities
export {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createVideo,
  createTroubleshooting,
  createRelatedLink,
  createFieldHelp,
  parseContentId,
  buildContentId,
  getParentContentId,
  isChildOf,
  filterByRole,
  formatDuration,
  formatLastUpdated,
} from './utils';
